import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { Button, FormField } from '@/shared/components';
import { AuthAlert } from './AuthAlert';
import { newPasswordSchema, PasswordRuleList, type NewPasswordValues } from './passwordRules';
import { PasswordInput } from './PasswordInput';

/**
 * Neues Passwort mit Bestätigung und den Regeln als Haken — für „Konto aktivieren“ und
 * „Passwort zurücksetzen“ dieselbe Fassung.
 */
export function NewPasswordForm({
  submitLabel,
  pendingLabel,
  failure,
  onSubmit,
}: {
  submitLabel: string;
  pendingLabel: string;
  failure: { title: string; text: string } | null;
  onSubmit: (password: string) => Promise<void>;
}) {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordValues>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });
  const password = useWatch({ control, name: 'password' });

  return (
    <form
      onSubmit={handleSubmit((values) => onSubmit(values.password))}
      className="flex flex-col gap-[18px]"
      noValidate
    >
      {failure && <AuthAlert title={failure.title} text={failure.text} />}
      <FormField label="Passwort" error={errors.password?.message} required>
        <PasswordInput {...register('password')} autoComplete="new-password" autoFocus />
      </FormField>
      <PasswordRuleList value={password} />
      <FormField label="Passwort bestätigen" error={errors.confirm?.message} required>
        <PasswordInput {...register('confirm')} autoComplete="new-password" />
      </FormField>
      <Button
        type="submit"
        variant="brand"
        size="lg"
        className="h-[54px] w-full text-base"
        loading={isSubmitting}
      >
        {isSubmitting ? pendingLabel : submitLabel}
      </Button>
    </form>
  );
}
